import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ==================== INTERFACES ====================

export interface Conversation {
  participants?: any;
  id: number;
  title: string;
  type: string;
  last_message_at: string;
  created_at?: string;
  updated_at?: string;
  last_message?: string;
  unread_count?: number;
}

export interface ConversationParticipant {
  id: number;
  conversation_id: number;
  user_id: string;
  last_read_at?: string;
  created_at?: string;
}

export interface Message {
  id: number;
  conversation_id: number;
  sender_id: string;
  content: string;
  message_type: string;
  created_at?: string;
  updated_at?: string;
  sender_name?: string;
}

export interface MessageCreateInput {
  conversation_id: number;
  // Matches the backend's `sendMessage` validator, which requires
  // `sender_id` (not `user_id`) — sending the wrong key fails validation.
  sender_id: string;
  content: string;
  message_type?: string;
}

export interface ConversationCreateInput {
  title: string;
  type: string;
  participants: any[];
  // The backend's `createConversation` validator requires this at the top
  // level (separate from `participants`) to identify who's creating the
  // conversation — omitting it fails validation.
  user_id: string;
}

// `payload.data || payload` breaks whenever `data` is present but falsy
// (`null`, as the backend sends for "no messages yet" on a brand-new
// conversation) — `||` treats that the same as "missing" and falls back to
// the whole {status, message, data} envelope, which then gets rendered as
// if it were the list itself. This guards against that by only ever
// returning an actual array.
const asArray = <T,>(payload: any): T[] => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

// Same idea for single-object responses — `??` (unlike `||`) only falls
// back when `data` is actually absent, not just falsy.
const asItem = <T,>(payload: any): T => payload?.data ?? payload;

// ==================== INITIAL STATE ====================

interface ChatState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: { [conversationId: number]: Message[] };
  loading: boolean;
  error: string | null;
  unreadCount: number;
}

const initialState: ChatState = {
  conversations: [],
  activeConversation: null,
  messages: {},
  loading: false,
  error: null,
  unreadCount: 0,
};

// ==================== ASYNC THUNKS ====================

export const getConversations = createAsyncThunk(
  'chat/getConversations',
  async (userId: string) => {
    const response = await api.get(`/chat/conversations/${userId}`);
    return response.data;
  }
);

export const getMessages = createAsyncThunk(
  'chat/getMessages',
  async ({ conversationId, userId }: { conversationId: number; userId: string }) => {
    const response = await api.get(`/chat/messages/${conversationId}/${userId}`);
    return response.data;
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (messageData: MessageCreateInput) => {
    const response = await api.post('/chat/send', messageData);
    return response.data;
  }
);

export const startConversation = createAsyncThunk(
  'chat/startConversation',
  async (conversationData: ConversationCreateInput) => {
    const response = await api.post('/chat/start', conversationData);
    return response.data;
  }
);

export const markAsRead = createAsyncThunk(
  'chat/markAsRead',
  async ({ messageId, userId }: { messageId: number; userId: string }) => {
    const response = await api.put(`/chat/read/${messageId}/${userId}`);
    return response.data;
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
    },
    addMessage: (state, action) => {
      const { conversationId, message } = action.payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      state.messages[conversationId].push(message);
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Conversations
      .addCase(getConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversations.fulfilled, (state, action) => {
        state.loading = false;
        const list = asArray<Conversation>(action.payload);
        state.conversations = list;
        // Calculate total unread count
        state.unreadCount = list.reduce((total: number, conv: Conversation) => total + (conv.unread_count || 0), 0);
      })
      .addCase(getConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      })
      
      // Get Messages
      .addCase(getMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.loading = false;
        // The API response has no conversationId of its own — it's just the
        // message list — so this must come from the thunk's original
        // argument (action.meta.arg), not action.payload. Keying off
        // action.payload.conversationId (always undefined) meant fetched
        // messages were stored under state.messages[undefined] and never
        // matched activeConversation.id, so the thread always looked empty.
        // asArray also covers a conversation with no messages yet, where
        // the backend sends `data: null` rather than `data: []`.
        state.messages[action.meta.arg.conversationId] = asArray<Message>(action.payload);
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      })

      // Send Message
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        // Same issue as getMessages — read the conversation id from the
        // thunk argument, not the response payload, and don't assume the
        // thread was already loaded (it won't be for a just-started
        // conversation with no prior getMessages call).
        const conversationId = action.meta.arg.conversation_id;
        const newMessage = asItem<Message>(action.payload);
        if (!state.messages[conversationId]) {
          state.messages[conversationId] = [];
        }
        state.messages[conversationId].push(newMessage);

        // Update conversation's last message
        const conversation = state.conversations.find(c => c.id === conversationId);
        if (conversation) {
          conversation.last_message = newMessage.content;
          // Message.created_at is optional (the type covers any payload
          // shape), but Conversation.last_message_at isn't — fall back to
          // now rather than assign a possibly-undefined value.
          conversation.last_message_at = newMessage.created_at || new Date().toISOString();
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send message';
      })

      // Start Conversation
      .addCase(startConversation.fulfilled, (state, action) => {
        const conversation = asItem<Conversation>(action.payload);
        state.conversations.unshift(conversation);
        state.activeConversation = conversation;
      })
      .addCase(startConversation.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to start conversation';
      })

      // Mark as Read
      .addCase(markAsRead.fulfilled, (state, action) => {
        // Same fix as above — the "messageId" argument here is actually the
        // conversation id (see how Chat.tsx calls this thunk), and the
        // response payload doesn't carry it back.
        const conversationId = action.meta.arg.messageId;
        const conversation = state.conversations.find(c => c.id === conversationId);
        if (conversation && conversation.unread_count) {
          state.unreadCount = Math.max(0, state.unreadCount - conversation.unread_count);
          conversation.unread_count = 0;
        }
      });
  },
});

export const { setActiveConversation, addMessage, clearError, updateUnreadCount } = chatSlice.actions;
export default chatSlice.reducer;
